import { useState, useCallback, useRef, useEffect } from 'react';
import { VibeCodeResponse } from '../types';
import { getVibeCodePromptId, generateVibeCodeFormat } from '../services/vibeCodeService';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';

interface UseVibeCodeReturn {
  result: VibeCodeResponse | null;
  isSubmitting: boolean;
  submit: (noteId: string) => Promise<void>;
  reset: () => void;
}

export const useVibeCode = (): UseVibeCodeReturn => {
  const [result, setResult] = useState<VibeCodeResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const promptIdRef = useRef<string | null>(null);
  const { showAlert } = useAlertStore();
  const cancelledRef = useRef(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    getVibeCodePromptId()
      .then((id) => {
        if (!cancelled) promptIdRef.current = id;
      })
      .catch((error) => {
        if (!cancelled) {
          errorReporter.expected('Failed to load vibe code format type', error, {
            feature: 'vibeCode',
            operation: 'loadPromptId',
          });
        }
      });

    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
  }, []);

  const submit = useCallback(
    async (noteId: string) => {
      if (submittingRef.current) return;

      const promptId = promptIdRef.current;
      if (!promptId) {
        showAlert('Vibe Code format is not available yet. Please try again later.', 'warning');
        return;
      }

      submittingRef.current = true;
      setIsSubmitting(true);
      setResult(null);

      try {
        const response = await generateVibeCodeFormat(noteId, promptId);
        if (!cancelledRef.current) setResult(response);
      } catch (error) {
        if (cancelledRef.current) return;

        const message = error instanceof Error ? error.message : '';

        if (message === 'BACKEND_NOT_AVAILABLE') {
          showAlert('This feature is not available yet. Please try again later.', 'warning');
        } else {
          showAlert('Failed to generate your vibe code prompt. Please try again.', 'error');
        }
      } finally {
        submittingRef.current = false;
        if (!cancelledRef.current) setIsSubmitting(false);
      }
    },
    [showAlert],
  );

  const reset = useCallback(() => {
    setResult(null);
    setIsSubmitting(false);
    submittingRef.current = false;
  }, []);

  return {
    result,
    isSubmitting,
    submit,
    reset,
  };
};
